/**
 * Import Tab für Schritt 2
 * Ermöglicht den Import von Transaktionsdaten über die Parqet API
 */

import React, { useState } from 'react';
import {
  SpaceBetween,
  Button,
  Alert,
  Box,
  Container,
  Header,
} from '@cloudscape-design/components';

import { TransaktionsDaten } from '@/types';
import { getParqetConfig } from '@/utils/parqetConfig';
import {
  buildAuthorizationUrl,
  getStoredTokens,
  setStoredTokens,
  clearStoredTokens,
  fetchParqetData,
  refreshAccessToken,
} from '@/services/parqetService';

interface ImportTabProps {
  onImportErfolg: (
    daten: TransaktionsDaten[],
    currentPricesByAsset?: Record<string, number>,
    logosByAsset?: Record<string, string>,
    soldAssetIdentifiers?: string[]
  ) => void;
  istImportAbgeschlossen: boolean;
  onImportReset?: () => void;
}

/**
 * Komponente für Parqet API Import
 */
const ImportTab: React.FC<ImportTabProps> = ({
  onImportErfolg,
  onImportReset
}) => {
  const [parqetStatus, setParqetStatus] = useState<{
    loading: boolean;
    error: string | null;
    success: boolean;
    warning?: string;
  }>({ loading: false, error: null, success: false });

  const handleParqetConnect = async () => {
    try {
      const url = await buildAuthorizationUrl();
      window.location.href = url;
    } catch (err) {
      setParqetStatus((prev) => ({
        ...prev,
        error: err instanceof Error ? err.message : 'Parqet-Verbindung fehlgeschlagen.',
      }));
    }
  };

  const handleParqetFetch = async () => {
    let tokens = getStoredTokens();
    if (!tokens) {
      setParqetStatus((prev) => ({ ...prev, error: 'Nicht mit Parqet verbunden.' }));
      return;
    }
    setParqetStatus((prev) => ({ ...prev, loading: true, error: null }));
    try {
      if (tokens.expiresAt && Date.now() >= tokens.expiresAt - 60_000 && tokens.refreshToken) {
        const data = await refreshAccessToken(tokens.refreshToken);
        setStoredTokens(data);
        tokens = { accessToken: data.access_token, refreshToken: data.refresh_token ?? null, expiresAt: Date.now() + (data.expires_in ?? 0) * 1000 };
      }
      const result = await fetchParqetData(tokens.accessToken);
      if (result.transaktionsDaten.length === 0 && result.warning) {
        setParqetStatus((prev) => ({ ...prev, loading: false, error: result.warning ?? null }));
        return;
      }
      onImportErfolg(result.transaktionsDaten, result.currentPricesByAsset, result.logosByAsset, result.soldAssetIdentifiers);
      setParqetStatus({
        loading: false,
        error: null,
        success: true,
        warning: result.warning,
      });
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Datenabruf fehlgeschlagen.';
      if (message.includes('401') && tokens.refreshToken) {
        try {
          const data = await refreshAccessToken(tokens.refreshToken);
          setStoredTokens(data);
          const retry = await fetchParqetData(data.access_token);
          onImportErfolg(retry.transaktionsDaten, retry.currentPricesByAsset, retry.logosByAsset, retry.soldAssetIdentifiers);
          setParqetStatus({ loading: false, error: null, success: true, warning: retry.warning });
          return;
        } catch {
          clearStoredTokens();
          setParqetStatus((prev) => ({ ...prev, loading: false, error: 'Bitte erneut mit Parqet verbinden.' }));
          return;
        }
      }
      setParqetStatus((prev) => ({ ...prev, loading: false, error: message }));
    }
  };

  const handleParqetTrennen = () => {
    clearStoredTokens();
    setParqetStatus({ loading: false, error: null, success: false });
  };

  const parqetConfig = getParqetConfig();
  const parqetTokens = getStoredTokens();

  return (
    <SpaceBetween direction="vertical" size="l">
      <Container
        header={
          <Header variant="h3">
            Transaktionsdaten importieren
          </Header>
        }
      >
        <SpaceBetween direction="vertical" size="m">
          <Box variant="p">
            Daten direkt von Parqet (connect.parqet.com) laden. Zuerst verbinden, dann Daten abrufen. Es werden auch die aktuellen Kurse der Assets von der Parqet API abgerufen. Aktuell werden nur die Transaktionstypen Kauf (Buy), Verkauf (Sell), Transfer-In und Transfer-Out importiert.
          </Box>

          <Alert
            type="warning"
            dismissAriaLabel="Schließen"
            header="Wichtiger rechtlicher Hinweis"
          >
            Der Import dient ausschließlich der Simulation auf Basis Ihrer eingegebenen Daten und stellt keine steuerliche Beratung dar. Die von Parqet importierten Daten werden nicht auf Vollständigkeit oder steuerliche Korrektheit geprüft und dienen ausschließlich der unverbindlichen Modellrechnung. Sie sind nicht für eine amtliche Steuerberechnung geeignet. Auch bei korrektem Import ersetzt die Nutzung dieses Tools keine steuerliche Bewertung oder Prüfung Ihrer Angaben.
          </Alert>

          {!parqetConfig.isConfigured && (
            <Alert type="info" header="Parqet nicht konfiguriert">
              Für den Parqet-Import werden VITE_PARQET_CLIENT_ID und VITE_PARQET_REDIRECT_URI benötigt.
            </Alert>
          )}
          {parqetConfig.isConfigured && !parqetTokens && (
            <SpaceBetween direction="horizontal" size="m">
              <Button variant="primary" onClick={handleParqetConnect}>
                Mit Parqet verbinden
              </Button>
            </SpaceBetween>
          )}
          {parqetConfig.isConfigured && parqetTokens && (
            <>
              <SpaceBetween direction="horizontal" size="m">
                <Button
                  variant="primary"
                  onClick={handleParqetFetch}
                  disabled={parqetStatus.loading}
                  loading={parqetStatus.loading}
                >
                  {parqetStatus.loading ? 'Lade...' : 'Daten von Parqet laden'}
                </Button>
                <Button onClick={handleParqetTrennen}>Parqet-Verbindung trennen</Button>
                {onImportReset && (
                  <Button onClick={onImportReset}>Import zurücksetzen</Button>
                )}
              </SpaceBetween>
              {parqetStatus.error && (
                <Alert type="error">
                  <strong>Fehler:</strong> {parqetStatus.error}
                </Alert>
              )}
              {parqetStatus.success && (
                <Alert type="success">
                  <strong>Parqet-Daten wurden importiert.</strong>
                  {parqetStatus.warning ? ` ${parqetStatus.warning}` : ''}
                  Sie können die Transaktionsdaten in der Registerkarte &quot;Transaktionsdaten&quot; einsehen.
                </Alert>
              )}
            </>
          )}
        </SpaceBetween>
      </Container>
    </SpaceBetween>
  );
};

export default ImportTab;
